/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ComponentSeries, ComponentType, Roles, SamplingAlgorithm } from '@cambridgeassessment/lib-ee-commons';
import { Button, Headline, Paragraph, TopModal } from '@cambridgeassessment/cambridge-ui';
import { useParams } from 'react-router';
import { deleteAllComponents } from '../../../../store/components/actions';
import Loader from '../../../../components/Loader/Loader';
import thunks from '../../../../store/components/thunks';
import { RootState } from '../../../../store';
import { StyledContainer } from '../../Content.styles';
import { ButtonText, StyledInfoText, StyledModalChildrenContainer } from '../Details/styles';
import { getActiveSeriesData } from '../../../../store/series/thunks';
import ComponentDeadline from './ComponentDeadline';
import { ComponentWithSeriesDates } from '../../../../types/Components';
import { checkIsAllowedSubmissionWindow } from './utils';
import SamplingStartDate from './SamplingStartDate';
import moment from 'moment';
import { ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT } from '../../../../constants';

interface RouteProps {
  componentId: string | undefined;
}

export const ComponentDeadlines = (): JSX.Element => {
  const dispatch = useDispatch();
  const params = useParams<RouteProps>();

  const { isLoading, component, hasError } = useSelector((state: RootState) => state.components);
  const { businessUnit } = useSelector((state: RootState) => state.userData);
  const { activeSeriesIds, seriesData } = useSelector((state: RootState) => state.series);
  const { selectedRole } = useSelector((state: RootState) => state.userData);

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const isUserSuperAdmin = selectedRole?.name === Roles.SUBMIT_SUPER_ADMIN;
  const componentHasActiveSeries = !!component?.series.some((series) => activeSeriesIds?.includes(series.id));

  useEffect(() => {
    dispatch(getActiveSeriesData());
  }, [dispatch, JSON.stringify(seriesData)]);

  useEffect(() => {
    if (!component && activeSeriesIds) {
      if (businessUnit?.id && activeSeriesIds.length) {
        dispatch(thunks.getComponentWithEntriesCheck(businessUnit.id, activeSeriesIds, params?.componentId ?? ''));
      } else {
        dispatch(thunks.getComponent(params?.componentId ?? ''));
      }
    }

    return () => {
      dispatch(deleteAllComponents());
    };
  }, [dispatch, activeSeriesIds, businessUnit?.id, component]);

  useEffect(() => {
    if (activeSeriesIds?.length && seriesData && component) {
      if (!isUserSuperAdmin && componentHasActiveSeries) {
        setIsDisabled(componentHasActiveSeries);
      }

      setModalOpen(!!component.hasEntries && componentHasActiveSeries);
    }
  }, [dispatch, activeSeriesIds, seriesData, component]);

  if (isLoading) {
    return <Loader />;
  }

  if (hasError || component == null) {
    return <div>No component found</div>;
  }

  const compId = component._id ?? '';

  const aggregatedComponentsWithSeries: ComponentWithSeriesDates[] = component.series.map((componentSerie) => {
    const series = seriesData?.find((serie) => serie.id === componentSerie.id);

    const { submissionWindows, startDate, endDate, marksSubmissionWindows } = series || {};

    return {
      ...componentSerie,
      startDate,
      endDate,
      submissionWindows,
      marksSubmissionWindows,
    };
  });

  const { componentSeriesWithSW, componentSeriesWithNoSW, componentSeriesWithMarksSW, componentSeriesWithNoMarksSW } =
    aggregatedComponentsWithSeries.reduce(
      (prev, serie) => {
        if (serie?.submissionWindow) {
          prev.componentSeriesWithSW.push(serie);
        } else {
          serie.submissionWindows = serie.submissionWindows?.filter((sw) =>
            checkIsAllowedSubmissionWindow(serie.startDate, serie.endDate, sw.buffer),
          );
          prev.componentSeriesWithNoSW.push(serie);
        }

        const marksSerie: Partial<ComponentWithSeriesDates> = {
          id: serie.id,
          name: serie.name,
          startDate: serie.startDate,
          endDate: serie.endDate,
          submissionWindow: serie.marksSubmissionWindow,
          submissionWindows: serie.marksSubmissionWindows,
        };

        if (serie?.marksSubmissionWindow) {
          prev.componentSeriesWithMarksSW.push(marksSerie);
        } else {
          marksSerie.submissionWindows = marksSerie.submissionWindows?.filter((sw) =>
            checkIsAllowedSubmissionWindow(marksSerie.startDate, marksSerie.endDate, sw.buffer),
          );
          prev.componentSeriesWithNoMarksSW.push(marksSerie);
        }

        return prev;
      },
      {
        componentSeriesWithSW: [],
        componentSeriesWithNoSW: [],
        componentSeriesWithMarksSW: [],
        componentSeriesWithNoMarksSW: [],
      } as Record<string, Partial<ComponentWithSeriesDates>[]>,
    );

  const availableComponentSeriesWithNoSW = componentSeriesWithNoSW.filter((it) => !!it?.submissionWindows?.length);

  const availableComponentSeriesWithNoMarksSW = componentSeriesWithNoMarksSW.filter(
    (it) => !!it?.submissionWindows?.length,
  );

  const startMaxDate = (series: Partial<ComponentWithSeriesDates>): string | undefined => {
    const currSeries = aggregatedComponentsWithSeries?.find((serie) => serie.id === series.id);
    return currSeries?.submissionWindow?.start;
  };

  const endMaxDate = (series: Partial<ComponentWithSeriesDates>): string | undefined => {
    const currSeries = aggregatedComponentsWithSeries?.find((serie) => serie.id === series.id);
    return currSeries?.submissionWindow?.end;
  };

  const updateSeriesStartDate = (seriesId: ComponentSeries['id'], startDate: string): void => {
    dispatch(thunks.updateSeries(compId, startDate, 'start', seriesId));
  };

  const updateSeriesEndDate = (seriesId: ComponentSeries['id'], endDate: string): void => {
    dispatch(thunks.updateSeries(compId, endDate, 'end', seriesId));
  };

  const updateSeriesMarksStartDate = (seriesId: ComponentSeries['id'], startDate: string): void => {
    dispatch(thunks.updateSeries(compId, startDate, 'marksSubmissionWindowStartDate', seriesId));

    const series = component.series.find((serie) => serie.id === seriesId);
    const seriesSalgoStartDate = series?.salgoStartDate && moment(series.salgoStartDate);

    if (series && seriesSalgoStartDate && moment(startDate).isAfter(seriesSalgoStartDate)) {
      const newSalgoStartDate = moment(startDate).toISOString();
      dispatch(thunks.updateSeries(compId, newSalgoStartDate, 'salgoStartDate', seriesId));
    }
  };

  const updateSeriesMarksEndDate = (seriesId: ComponentSeries['id'], endDate: string): void => {
    dispatch(thunks.updateSeries(compId, endDate, 'marksSubmissionWindowEndDate', seriesId));
  };

  const updatePersistStartDateFlag = (seriesId: ComponentSeries['id'], persistStartDate: boolean): void => {
    dispatch(thunks.updateSeries(compId, persistStartDate, 'persistStartDate', seriesId));
  };

  const updatePersistEndDateFlag = (seriesId: ComponentSeries['id'], persistEndDate: boolean): void => {
    dispatch(thunks.updateSeries(compId, persistEndDate, 'persistEndDate', seriesId));
  };

  const updateMarksPersistStartDateFlag = (seriesId: ComponentSeries['id'], persistStartDate: boolean): void => {
    dispatch(thunks.updateSeries(compId, persistStartDate, 'marksSubmissionWindowPersistStartDate', seriesId));
  };

  const updateMarksPersistEndDateFlag = (seriesId: ComponentSeries['id'], persistEndDate: boolean): void => {
    dispatch(thunks.updateSeries(compId, persistEndDate, 'marksSubmissionWindowPersistEndDate', seriesId));
  };

  const updateSalgoStartDate = (seriesId: ComponentSeries['id'], startDate: string): void => {
    dispatch(thunks.updateSeries(compId, startDate, 'salgoStartDate', seriesId));
  };

  const createSeriesSubmissionWindow = (
    seriesId: ComponentSeries['id'],
    submissionWindow: ComponentSeries['submissionWindow'],
  ): void => {
    dispatch(thunks.updateSeries(compId, submissionWindow, 'submissionWindow', seriesId));
  };

  const createSeriesMarksSubmissionWindow = (
    seriesId: ComponentSeries['id'],
    submissionWindow: ComponentSeries['submissionWindow'],
  ): void => {
    dispatch(thunks.updateSeries(compId, submissionWindow, 'marksSubmissionWindow', seriesId));
  };

  const deleteSeriesMarksSubmissionWindow = (seriesId: number): void => {
    dispatch(thunks.deleteSeriesSubmissionWindow(compId, seriesId, 'marksSubmissionWindow'));
  };

  const deleteSeriesSamplesSubmissionWindow = (seriesId: number): void => {
    dispatch(thunks.deleteSeriesSubmissionWindow(compId, seriesId, 'submissionWindow'));
  };

  const isComponentConfigDisabled =
    !component.samplingAlgorithm ||
    component.samplingAlgorithm === SamplingAlgorithm.NoSampling ||
    (componentHasActiveSeries && !isUserSuperAdmin);

  return (
    <StyledContainer data-qa-id={`component-deadlines-page-${component.number}`}>
      {component.hasEntries && (
        <TopModal topPosition={64} dataQaId='entries-received-active-series' open={isModalOpen} fixed={false}>
          <StyledModalChildrenContainer>
            <div data-qa-id='entries-received-active-series-text'>{ENTRIES_RECEIVED_ACTIVE_SERIES_TEXT}</div>
            <Button className='closeModal' onClick={() => setModalOpen(false)} qaId='close-modal' variant='text'>
              <ButtonText>Close</ButtonText>
            </Button>
          </StyledModalChildrenContainer>
        </TopModal>
      )}
      <Headline data-qa-id='component-deadlines-title'>Deadlines for the component</Headline>
      <Paragraph data-qa-id='component-deadlines-subtext'>
        Configure component <b>{`Component ${component.number} - ${component.name}`}</b> deadlines
      </Paragraph>
      {component.type === ComponentType.MODERATED && (
        <SamplingStartDate
          role={selectedRole}
          series={component.series}
          onSalgoStartDateUpdate={updateSalgoStartDate}
          disabled={isComponentConfigDisabled}
        />
      )}
      {component.type === ComponentType.MODERATED && (
        <ComponentDeadline
          title={'Marks submission dates'}
          component={component}
          dataQaId='marks'
          componentSeriesWithSW={componentSeriesWithMarksSW}
          componentSeriesWithNoSW={availableComponentSeriesWithNoMarksSW}
          isDisabled={isDisabled}
          onSeriesStartDateUpdate={updateSeriesMarksStartDate}
          onSeriesEndDateUpdate={updateSeriesMarksEndDate}
          onPersistStartDateFlagUpdate={updateMarksPersistStartDateFlag}
          onPersistEndDateFlagUpdate={updateMarksPersistEndDateFlag}
          onCreateSeriesSubmissionWindow={createSeriesMarksSubmissionWindow}
          onDeleteSeriesSubmissionWindow={deleteSeriesMarksSubmissionWindow}
          startMaxDate={startMaxDate}
          endMaxDate={endMaxDate}
        />
      )}
      {component.type === ComponentType.MODERATED && (
        <StyledInfoText data-qa-id='sample-sw-creation'>
          Please create a corresponding sample submission window
        </StyledInfoText>
      )}
      <ComponentDeadline
        title={component.type === ComponentType.MODERATED ? 'Sample submission dates' : undefined}
        component={component}
        dataQaId='sample'
        componentSeriesWithSW={componentSeriesWithSW}
        componentSeriesWithNoSW={availableComponentSeriesWithNoSW}
        isDisabled={componentHasActiveSeries && !isUserSuperAdmin}
        onSeriesStartDateUpdate={updateSeriesStartDate}
        onSeriesEndDateUpdate={updateSeriesEndDate}
        onPersistStartDateFlagUpdate={updatePersistStartDateFlag}
        onPersistEndDateFlagUpdate={updatePersistEndDateFlag}
        onCreateSeriesSubmissionWindow={createSeriesSubmissionWindow}
        onDeleteSeriesSubmissionWindow={deleteSeriesSamplesSubmissionWindow}
      />
    </StyledContainer>
  );
};
